import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { message } from 'antd'
import { projectApi, outreachApi, replyApi, accountApi, statsApi, configApi } from '../api/client'
import { useAppStore } from '../store'
import type {
  Project,
  ProjectFilters,
  OutreachRecord,
  Reply,
  AccountState,
  DailyStats,
  DashboardStats,
  OutreachTask,
  SystemConfig,
} from '../types'

// ==================== 项目相关 Hooks ====================

export function useProjects(filters?: ProjectFilters) {
  return useQuery({
    queryKey: ['projects', filters],
    queryFn: () => projectApi.getList(filters),
  })
}

export function useProject(id: string) {
  return useQuery({
    queryKey: ['project', id],
    queryFn: () => projectApi.getById(id),
    enabled: !!id,
  })
}

export function useCreateProject() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: (project: Partial<Project>) => projectApi.create(project),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] })
      message.success('项目创建成功')
    },
  })
}

export function useUpdateProject() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Project> }) =>
      projectApi.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['projects'] })
      queryClient.invalidateQueries({ queryKey: ['project', id] })
      message.success('项目更新成功')
    },
  })
}

export function useDeleteProject() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => projectApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] })
      message.success('项目删除成功')
    },
  })
}

export function useRescoreProject() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => projectApi.rescore(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['projects'] })
      queryClient.invalidateQueries({ queryKey: ['project', id] })
      message.success('重新评分完成')
    },
  })
}

export function useBatchRescore() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (ids: string[]) => projectApi.batchRescore(ids),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['projects'] })
      message.success(`批量评分完成：成功 ${result.success}，失败 ${result.failed}`)
    },
  })
}

export function useBatchDelete() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (ids: string[]) => projectApi.batchDelete(ids),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['projects'] })
      message.success(`批量删除完成：已删除 ${result.deleted} 个项目`)
    },
  })
}

export function useMoveToColdPool() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => projectApi.moveToColdPool(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] })
      message.success('已移入冷池')
    },
  })
}

export function useRestoreFromColdPool() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => projectApi.restoreFromColdPool(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] })
      message.success('已从冷池恢复')
    },
  })
}

// ==================== 触达相关 Hooks ====================

export function useOutreachRecords(params?: {
  project_id?: string
  channel?: string
  status?: string
  page?: number
  page_size?: number
}) {
  return useQuery({
    queryKey: ['outreach', params],
    queryFn: () => outreachApi.getList(params),
  })
}

export function useOutreachRecord(id: string) {
  return useQuery({
    queryKey: ['outreach', id],
    queryFn: () => outreachApi.getById(id),
    enabled: !!id,
  })
}

export function useCreateOutreachTask() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: (task: OutreachTask) => outreachApi.createTask(task),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['outreach'] })
      queryClient.invalidateQueries({ queryKey: ['projects'] })
      message.success(`任务创建完成：成功 ${result.success}，失败 ${result.failed}`)
    },
  })
}

export function usePreviewOutreach() {
  return useMutation({
    mutationFn: (params: {
      project_id: string
      channel: string
      phase: number
      template?: string
    }) => outreachApi.preview(params),
  })
}

export function useSendOutreach() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (params: {
      record_id: string
      project_id?: string
      channel?: string
      phase?: number
      content?: string
    }) => outreachApi.send(params),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['outreach'] })
      queryClient.invalidateQueries({ queryKey: ['projects'] })
      if (data.success) {
        message.success(
          `发送成功！发件人: ${data.sender} → 收件人: ${data.recipient}`,
          5
        )
      } else {
        message.error(`发送失败: ${data.error || '未知错误'}`, 5)
      }
    },
    onError: (error: Error) => {
      message.error(`发送请求失败: ${error.message}`, 5)
    },
  })
}

export function useRetryOutreach() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => outreachApi.retry(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['outreach'] })
      message.success('重试成功')
    },
  })
}

export function useCancelOutreach() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => outreachApi.cancel(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['outreach'] })
      message.success('已取消')
    },
  })
}

export function useQueueStatus() {
  return useQuery({
    queryKey: ['outreach', 'queue'],
    queryFn: () => outreachApi.getQueueStatus(),
    refetchInterval: 10000, // 每10秒刷新
  })
}

// ==================== 回复相关 Hooks ====================

export function useReplies(params?: {
  project_id?: string
  channel?: string
  intent?: string
  is_processed?: boolean
  page?: number
  page_size?: number
}) {
  return useQuery({
    queryKey: ['replies', params],
    queryFn: () => replyApi.getList(params),
  })
}

export function useReply(id: string) {
  return useQuery({
    queryKey: ['reply', id],
    queryFn: () => replyApi.getById(id),
    enabled: !!id,
  })
}

export function useMarkReplyProcessed() {
  const queryClient = useQueryClient()
  const setUnprocessedCount = useAppStore((state) => state.setUnprocessedRepliesCount)

  return useMutation({
    mutationFn: ({ id, params }: { id: string; params?: { intent?: string; notes?: string } }) =>
      replyApi.markProcessed(id, params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['replies'] })
      // 更新未处理数量
      replyApi.getUnprocessedCount().then((data) => {
        setUnprocessedCount(data.total)
      })
      message.success('已标记为已处理')
    },
  })
}

export function useBatchProcessReplies() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ ids, intent }: { ids: string[]; intent?: string }) =>
      replyApi.batchProcess(ids, intent),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['replies'] })
      message.success(`批量处理完成：成功 ${result.success}，失败 ${result.failed}`)
    },
  })
}

export function useUnprocessedRepliesCount() {
  const setUnprocessedCount = useAppStore((state) => state.setUnprocessedRepliesCount)
  
  return useQuery({
    queryKey: ['replies', 'unprocessed-count'],
    queryFn: async () => {
      const data = await replyApi.getUnprocessedCount()
      setUnprocessedCount(data.total)
      return data
    },
    refetchInterval: 30000, // 每30秒刷新
  })
}

// ==================== 账号相关 Hooks ====================

export function useAccounts() {
  const setAccounts = useAppStore((state) => state.setAccounts)
  
  return useQuery({
    queryKey: ['accounts'],
    queryFn: async () => {
      const accounts = await accountApi.getList()
      setAccounts(accounts)
      return accounts
    },
    refetchInterval: 60000, // 每分钟刷新
  })
}

export function useAccount(id: string) {
  return useQuery({
    queryKey: ['account', id],
    queryFn: () => accountApi.getById(id),
    enabled: !!id,
  })
}

export function useCreateAccount() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: (account: Partial<AccountState>) => accountApi.create(account),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['accounts'] })
      message.success('账号创建成功')
    },
  })
}

export function useUpdateAccount() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<AccountState> }) =>
      accountApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['accounts'] })
      message.success('账号更新成功')
    },
  })
}

export function useDeleteAccount() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => accountApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['accounts'] })
      message.success('账号删除成功')
    },
  })
}

export function useTestAccountConnection() {
  return useMutation({
    mutationFn: (id: string) => accountApi.testConnection(id),
    onSuccess: (result) => {
      if (result.success) {
        message.success('连接测试成功')
      } else {
        message.error(`连接测试失败：${result.message}`)
      }
    },
  })
}

export function useResetDailyUsage() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id?: string) => accountApi.resetDailyUsage(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['accounts'] })
      message.success('每日使用量已重置')
    },
  })
}

// ==================== 统计相关 Hooks ====================

export function useDashboardStats() {
  const setDashboardStats = useAppStore((state) => state.setDashboardStats)
  
  return useQuery({
    queryKey: ['stats', 'dashboard'],
    queryFn: async () => {
      const stats = await statsApi.getDashboard()
      setDashboardStats(stats)
      return stats
    },
    refetchInterval: 30000, // 每30秒刷新
  })
}

export function useDailyStats(params?: { start_date?: string; end_date?: string }) {
  return useQuery({
    queryKey: ['stats', 'daily', params],
    queryFn: () => statsApi.getDaily(params),
  })
}

export function useChannelStats(params?: { start_date?: string; end_date?: string }) {
  return useQuery({
    queryKey: ['stats', 'channel', params],
    queryFn: () => statsApi.getByChannel(params),
  })
}

export function useFunnelStats() {
  return useQuery({
    queryKey: ['stats', 'funnel'],
    queryFn: () => statsApi.getFunnel(),
  })
}

export function useTrendStats(params: {
  metric: string
  period: 'day' | 'week' | 'month'
  days?: number
}) {
  return useQuery({
    queryKey: ['stats', 'trends', params],
    queryFn: () => statsApi.getTrends(params),
  })
}

// ==================== 配置相关 Hooks ====================

export function useSystemConfig() {
  const setSystemConfig = useAppStore((state) => state.setSystemConfig)
  
  return useQuery({
    queryKey: ['config'],
    queryFn: async () => {
      const config = await configApi.get()
      setSystemConfig(config)
      return config
    },
  })
}

export function useUpdateConfig() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: (config: Partial<SystemConfig>) => configApi.update(config),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['config'] })
      message.success('配置更新成功')
    },
  })
}

export function useTemplates(channel: string) {
  return useQuery({
    queryKey: ['templates', channel],
    queryFn: () => configApi.getTemplates(channel),
    enabled: !!channel,
  })
}

export function useUpdateTemplate() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: ({ channel, name, content }: { channel: string; name: string; content: string }) =>
      configApi.updateTemplate(channel, name, content),
    onSuccess: (_, { channel }) => {
      queryClient.invalidateQueries({ queryKey: ['templates', channel] })
      message.success('模板更新成功')
    },
  })
}

// ==================== 工具 Hooks ====================

// 自动刷新 Hook
export function useAutoRefresh(callback: () => void, intervalMs: number, enabled = true) {
  const preferences = useAppStore((state) => state.preferences)
  const interval = preferences.autoRefreshInterval * 1000 || intervalMs
  
  useQuery({
    queryKey: ['auto-refresh', callback.toString()],
    queryFn: async () => {
      callback()
      return null
    },
    refetchInterval: enabled ? interval : false,
    enabled,
  })
}

// 实时状态 Hook
export function useRealtimeStatus() {
  const { data: queueStatus } = useQueueStatus()
  const { data: unprocessedCount } = useUnprocessedRepliesCount()
  const { data: accounts } = useAccounts()
  
  return {
    queueStatus,
    unprocessedRepliesCount: unprocessedCount?.total ?? 0,
    accountsHealth: accounts?.filter((a) => a.health === 'healthy').length ?? 0,
    totalAccounts: accounts?.length ?? 0,
  }
}
